import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Divider,
  CircularProgress,
  LinearProgress,
  Checkbox,
  FormControlLabel,
  Stack,
  Link,
} from '@mui/material';
import {
  Warning as WarningIcon,
  CheckCircle as AccountedIcon,
  Cancel as NotAccountedIcon,
  Groups as TotalIcon,
  Lock as LockIcon,
  Schedule as ScheduleIcon,
  NoteAdd as NoteIcon,
} from '@mui/icons-material';
import { appColors } from '../../Config/Theme';
import { getProgressColor } from '../../Utils/dashboardHelpers';
import IncidentTypeSelector from './IncidentTypeSelector';
import { IncidentType } from '../../Types/enums';

interface CloseIncidentDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (notes: string, incidentType?: string) => Promise<void>;
  incidentId: string;
  incidentName: string;
  incidentType?: string | null;
  total: number;
  accounted: number;
  notAccounted: number;
  notChecked?: number;
  percentComplete: number;
  unsyncedChanges: number;
  isClosed: boolean;
}

const StatRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: number;
  color: string;
}> = ({ icon, label, value, color }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75 }}>
    <Box sx={{ color, display: 'flex' }}>{icon}</Box>
    <Typography variant="body2" sx={{ flex: 1, color: appColors.text.body }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 700, color }}>
      {value}
    </Typography>
  </Box>
);

const CloseIncidentDialog: React.FC<CloseIncidentDialogProps> = ({
  open,
  onClose,
  onConfirm,
  incidentId,
  incidentName,
  incidentType,
  total,
  accounted,
  notAccounted,
  notChecked = 0,
  percentComplete,
  unsyncedChanges,
  isClosed,
}) => {
  const navigate = useNavigate();
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const generation = useRef(0);
  const hasSelectedType = Object.values(IncidentType).includes(incidentType?.trim().toLowerCase() as IncidentType);
  const needsType = !hasSelectedType;

  useEffect(() => {
    generation.current += 1;
    setNotes('');
    setSubmitting(false);
    setError(null);
    setAcknowledged(false);
    setSelectedType('');
    return () => { generation.current += 1; };
  }, [open, incidentId, incidentType, isClosed]);

  const hasUnsyncedChanges = unsyncedChanges > 0;
  const hasUnresolved = notAccounted > 0 || notChecked > 0;
  const unresolvedCount = notAccounted + notChecked;
  const pct = percentComplete;
  const progressColor = getProgressColor(pct);
  const MAX_NOTES = 250;

  const nowFormatted = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date());

  const canClose = open && !submitting && !hasUnsyncedChanges && !isClosed && (!hasUnresolved || acknowledged)
    && (!needsType || Object.values(IncidentType).includes(selectedType as IncidentType));

  const handleConfirm = async () => {
    if (!canClose) return;
    const requestGeneration = generation.current;
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(notes, needsType ? selectedType : undefined);
    } catch (err) {
      if (requestGeneration !== generation.current) return;
      setError(err instanceof Error ? err.message : 'Failed to close incident.');
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setNotes('');
    setError(null);
    setAcknowledged(false);
    onClose();
  };

  const handleReviewRemaining = () => {
    if (submitting) return;
    handleClose();
    navigate(`/incidents/${incidentId}/unaccounted`);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: { sx: { borderRadius: '12px', border: `2px solid ${appColors.status.error}`, maxHeight: '95vh' } },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 0.5 }}>
        <LockIcon sx={{ color: appColors.status.error }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: appColors.text.heading, flex: 1 }}>
          Close Incident
        </Typography>
        {!isClosed && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ScheduleIcon sx={{ fontSize: 14, color: appColors.text.caption }} />
            <Typography variant="caption" sx={{ color: appColors.text.caption, whiteSpace: 'nowrap' }}>
              Now ({nowFormatted})
            </Typography>
          </Box>
        )}
      </DialogTitle>

      <DialogContent sx={{ py: 1 }}>
        {/* Incident name */}
        <Typography variant="subtitle2" sx={{ color: appColors.text.body, mb: 1.5 }}>
          {incidentName}
        </Typography>

        {/* Already closed guard */}
        {isClosed && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This incident is already closed.
          </Alert>
        )}

        {!isClosed && needsType && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Incident Type <Box component="span" sx={{ color: appColors.status.error }}>*</Box>
            </Typography>
            <Typography variant="body2" sx={{ color: appColors.text.body, mb: 1 }}>
              Select an incident type before closing this incident.
            </Typography>
            <Box aria-disabled={submitting} sx={{ pointerEvents: submitting ? 'none' : 'auto', opacity: submitting ? 0.6 : 1 }}>
              <IncidentTypeSelector value={selectedType} onChange={(value) => { if (!submitting) setSelectedType(value); }} />
            </Box>
          </Box>
        )}

        {/* Unsynced warning — blocks closing */}
        {!isClosed && hasUnsyncedChanges && (
          <Alert severity="error" icon={<WarningIcon />} sx={{ mb: 2 }}>
            <strong>{unsyncedChanges} unsynced change{unsyncedChanges !== 1 ? 's' : ''} remaining.</strong>
            <br />
            Wait for all changes to sync before closing the incident.
          </Alert>
        )}

        {/* Status-aware warning / success message */}
        {!isClosed && !hasUnsyncedChanges && hasUnresolved && (
          <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 1.5, py: 0.5 }}>
            <strong>
              {notAccounted > 0 && `${notAccounted} Not Accounted`}              {notAccounted > 0 && notChecked > 0 && ', '}
              {notChecked > 0 && `${notChecked} Not Checked`}            </strong>
            {' — Closing is final. Statuses cannot be updated after.'}
          </Alert>
        )}

        {!isClosed && !hasUnsyncedChanges && !hasUnresolved && (
          <Alert severity="success" icon={<AccountedIcon />} sx={{ mb: 2 }}>
            All people are accounted for. You can close this incident.
          </Alert>
        )}

        {/* Accountability summary with progress bar */}
        <Box
          sx={{
            bgcolor: appColors.surface.card,
            borderRadius: '8px',
            border: `1px solid ${appColors.border.default}`,
            px: 2,
            py: 1,
            mb: 1.5,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700, color: appColors.text.caption, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Accountability — {pct}%
          </Typography>
          <LinearProgress
            variant="determinate"
            value={pct}
            sx={{
              height: 8,
              borderRadius: 4,
              mt: 1,
              mb: 1.5,
              bgcolor: appColors.border.default,
              '& .MuiLinearProgress-bar': { bgcolor: progressColor, borderRadius: 4 },
            }}
          />
          <Stack spacing={0}>
            <StatRow icon={<TotalIcon fontSize="small" />} label="To Account For" value={total} color={appColors.primary.main} />
            <StatRow icon={<AccountedIcon fontSize="small" />} label="Accounted" value={accounted} color={appColors.status.success} />
            <StatRow icon={<NotAccountedIcon fontSize="small" />} label="Not Accounted" value={notAccounted} color={appColors.status.error} />
          </Stack>
          {unresolvedCount > 0 && (
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 600, color: appColors.status.error }}>
              {unresolvedCount} {unresolvedCount === 1 ? 'person' : 'people'} still require follow-up
            </Typography>
          )}
        </Box>

        {/* Review remaining link */}
        {!isClosed && unresolvedCount > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Link
              component="button"
              variant="body2"
              underline="hover"
              onClick={handleReviewRemaining}
              aria-disabled={submitting}
              sx={{ fontWeight: 600, color: appColors.primary.accent }}
            >
              Review remaining people before closing →
            </Link>
          </Box>
        )}

        <Divider sx={{ mb: 1.5 }} />

        {/* Acknowledgement checkbox — only when unresolved */}
        {!isClosed && hasUnresolved && !hasUnsyncedChanges && (
          <FormControlLabel
            control={
              <Checkbox
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                color="error"
                disabled={submitting}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: appColors.text.heading, fontWeight: 500 }}>
                I understand people are still unresolved and want to close this incident.
              </Typography>
            }
            sx={{ mb: 1.5, alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 0.25 } }}
          />
        )}

        {/* Closing notes — styled like Create Incident notes */}
        {!isClosed && (
          <>
            <Typography variant="body2" sx={{ fontWeight: 600, color: appColors.text.heading, mb: 0.5 }}>
              Closing Notes{' '}
              <Typography component="span" variant="caption" sx={{ color: appColors.text.placeholder }}>(optional)</Typography>
            </Typography>
            <Box sx={{ position: 'relative', mb: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  border: `1px solid ${appColors.border.default}`,
                  borderRadius: '8px',
                  bgcolor: appColors.surface.card,
                  minHeight: 64,
                  '&:hover': { borderColor: appColors.border.hover },
                  '&:focus-within': { borderColor: appColors.primary.main, borderWidth: 2 },
                }}
              >
                <Box sx={{ pl: 1.5, pt: 1.5, flexShrink: 0 }}>
                  <NoteIcon sx={{ fontSize: 18, color: appColors.text.placeholder }} />
                </Box>
                <textarea
                  placeholder="Document outcome, unresolved issues, or follow-up actions…"
                  value={notes}
                  onChange={(e) => { if (e.target.value.length <= MAX_NOTES) setNotes(e.target.value); }}
                  rows={2}
                  disabled={submitting}
                  style={{
                    flex: 1,
                    border: 'none',
                    outline: 'none',
                    resize: 'none',
                    padding: '10px 12px 18px 8px',
                    fontFamily: 'inherit',
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                    color: appColors.text.heading,
                    backgroundColor: 'transparent',
                    opacity: submitting ? 0.5 : 1,
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                sx={{ position: 'absolute', bottom: 4, right: 8, color: appColors.text.placeholder }}
              >
                {notes.length} / {MAX_NOTES}
              </Typography>
            </Box>
          </>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={handleClose} disabled={submitting} variant="outlined">
          Cancel
        </Button>
        {!isClosed && (
          <Button
            onClick={handleConfirm}
            disabled={!canClose}
            variant="contained"
            color="error"
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <LockIcon />}
            sx={{ minHeight: 42 }}
          >
            {submitting ? 'Closing...' : 'Close Incident'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CloseIncidentDialog;
